import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { apiClient } from '../../services/apiClient'
import { RENEWAL_WARNING_DAYS, daysUntil } from '../../services/suscripcionClient'

type BroadcastMessage = {
  id: number
  message: string
  start_date: string
  end_date: string
}
type DashboardStats = {
  total_reports: number
  open_reports: number
  closed_this_month: number
  ai_enhancements_remaining: number | null
}
type PlanInfo = {
  name: string
  current_period_end: string
}

function formatDate(dateStr: string): string {
  const datePart = dateStr.split('T')[0].split(' ')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${day} ${months[month - 1]} ${year}`
}

function renewalMessage(planName: string, daysLeft: number): string {
  if (daysLeft < 0) return `Tu plan ${planName} venció. Renueva para recuperar acceso a las funciones de tu plan.`
  if (daysLeft === 0) return `Tu plan ${planName} vence hoy. Renueva para no perder acceso a las funciones de tu plan.`
  if (daysLeft === 1) return `Tu plan ${planName} vence mañana. Renueva para no perder acceso a las funciones de tu plan.`
  return `Tu plan ${planName} vence en ${daysLeft} días. Renueva para no perder acceso a las funciones de tu plan.`
}

export function DashboardPage() {
  const { state } = useAuth()
  const [broadcasts, setBroadcasts] = useState<BroadcastMessage[]>([])
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [plan, setPlan] = useState<PlanInfo | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [bcResult, stResult] = await Promise.allSettled([
        apiClient.get<{ items: BroadcastMessage[] }>('/dashboard/broadcast'),
        apiClient.get<{ stats: DashboardStats; plan: PlanInfo | null }>('/dashboard/stats'),
      ])
      if (cancelled) return
      if (bcResult.status === 'fulfilled') setBroadcasts(bcResult.value.items)
      if (stResult.status === 'fulfilled') {
        setStats(stResult.value.stats)
        setPlan(stResult.value.plan)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  if (state.status !== 'authenticated') return null
  const { user } = state

  const aiValue = stats === null
    ? '—'
    : stats.ai_enhancements_remaining === null ? '∞' : String(stats.ai_enhancements_remaining)
  const daysLeft = plan ? daysUntil(plan.current_period_end) : null

  return (
    <div className="content-stack">
      <section className="card welcome-card">
        <p className="muted">Bienvenido</p>
        <h2>
          {user.first_name} {user.last_name_paternal}
        </h2>
        <p className="muted">{user.email}</p>
        {plan && (
          <p className="muted" style={{ marginTop: '0.6rem', fontSize: '0.85rem' }}>
            Plan: <strong style={{ color: 'var(--text)' }}>{plan.name}</strong>
            {' · '}
            Vigente hasta: <strong style={{ color: 'var(--text)' }}>{formatDate(plan.current_period_end)}</strong>
          </p>
        )}
      </section>

      {plan && daysLeft !== null && daysLeft <= RENEWAL_WARNING_DAYS && (
        <div className="alert alert--warning">
          {renewalMessage(plan.name, daysLeft)}{' '}
          <Link to="/suscripcion">Ir a Suscripción</Link>
        </div>
      )}

      {broadcasts.length > 0 && (
        <section className="broadcast-list">
          {broadcasts.map(b => (
            <div key={b.id} className="broadcast-item">
              {b.message}
            </div>
          ))}
        </section>
      )}

      <section className="stat-grid">
        <div className="stat-card">
          <span className="stat-card__value">{stats?.total_reports ?? '—'}</span>
          <span className="stat-card__label">Total reportes</span>
        </div>
        <div className="stat-card stat-card--accent">
          <span className="stat-card__value">{stats?.open_reports ?? '—'}</span>
          <span className="stat-card__label">Abiertos</span>
        </div>
        <div className="stat-card stat-card--success">
          <span className="stat-card__value">{stats?.closed_this_month ?? '—'}</span>
          <span className="stat-card__label">Cerrados este mes</span>
        </div>
        <div className="stat-card stat-card--warning">
          <span className="stat-card__value">{aiValue}</span>
          <span className="stat-card__label">Mejoras IA restantes</span>
        </div>
      </section>
    </div>
  )
}
